import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-1041" data-mp="2hvm1w11ux880">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1042" data-mp="23wmfz15qb7fj">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-1043" data-mp="28w8v5g54axqz">
          <p className="text-sm font-medium text-teal mb-5" data-id="element-1044" data-mp="2ms305o2rt2yt">
            SyNeural Frameworks
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-[1.1] mb-4 text-balance" data-id="element-1045" data-mp="3fil56un21eev">
            Human Capability Framework (HCF)
          </h1>
          <p className="text-xl lg:text-2xl text-navy font-medium leading-snug mb-8 max-w-xl" data-id="element-1046" data-mp="3kb4jxuxhafl9">
            Developing reasoning, judgment and readiness for responsibility.
          </p>

          <p className="text-lg text-ink-muted mb-10 leading-relaxed max-w-xl" data-id="element-1047" data-mp="2bbu4rc0xie5b">
            The Human Capability Framework defines how assessment, human review,
            development and reassessment connect. Its methods inform Noetivra’s
            Capability module, including CJI and NCJI workflows where they are
            appropriate to the program.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-1048" data-mp="2u3wo4v4yg8j9">
            <a href="/cji" className="bg-navy hover:bg-navy-light text-white font-medium px-6 py-3.5 rounded-md transition-colors duration-150 shadow-sm flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-1049" data-mp="384phvdnfmzlb">
              Explore CJI
              <ArrowRight size={18} data-id="element-1050" data-mp="18r3f03xuve1n" />
            </a>
            <a href="/ncji" className="bg-transparent border border-navy text-navy hover:bg-surface-muted font-medium px-6 py-3.5 rounded-md transition-colors duration-150 flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-1051" data-mp="18sms5lo7t8z7">
              Explore NCJI
              <ArrowRight size={18} data-id="element-1052" data-mp="3ipxtmbswepnw" />
            </a>
          </div>

          <p className="text-xs text-ink-muted font-medium" data-id="element-1053" data-mp="3mtcjrvajwmf2">
            For education, civil society, workforce systems, public
            institutions, and leadership development.
          </p>
        </motion.div>

        {/* Right Column: Abstract Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative h-[400px] lg:h-[500px] w-full flex items-center justify-center" data-id="element-1054" data-mp="5cmbysqsk39e">

          <svg viewBox="0 0 500 500" className="w-full h-full max-w-lg" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1055" data-mp="23ueole0j1uqs">
            {/* Background structural lines */}
            <circle cx="250" cy="250" r="200" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="4 8" data-id="element-1056" data-mp="1usyez8hjdwnm" />
            <circle cx="250" cy="250" r="140" stroke="#D9E2EC" strokeWidth="1" data-id="element-1057" data-mp="2ml5u24he90zw" />
            <circle cx="250" cy="250" r="80" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="2 4" data-id="element-1058" data-mp="2dsm2tp3flyp3" />

            {/* Connecting Pathways */}
            <path d="M100 350 C 150 350, 200 250, 250 250" stroke="#245B8F" strokeWidth="1.5" className="opacity-60" data-id="element-1059" data-mp="369yfushy9jt9" />
            <path d="M250 250 C 300 250, 350 150, 400 150" stroke="#2E8C88" strokeWidth="1.5" className="opacity-60" data-id="element-1060" data-mp="2l25szzxx3q58" />
            <path d="M150 150 C 200 200, 250 250, 300 350" stroke="#123B5D" strokeWidth="1.5" className="opacity-40" data-id="element-1061" data-mp="2c292dsrywh7j" />

            {/* Nodes & Labels */}
            {/* Individual */}
            <g transform="translate(100, 350)" data-id="element-1062" data-mp="34rgb1kl4yn6i">
              <circle cx="0" cy="0" r="6" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-1063" data-mp="38srrp0m17y2k" />
              <text x="-10" y="20" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-1064" data-mp="2zzepf822vk3t">
                Individual
              </text>
            </g>

            {/* Education */}
            <g transform="translate(150, 150)" data-id="element-1065" data-mp="5o3ss2efov90">
              <circle cx="0" cy="0" r="8" fill="#FFFFFF" stroke="#6FAE8A" strokeWidth="2" data-id="element-1066" data-mp="1obdl6ujbquo" />
              <text x="0" y="-15" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-1067" data-mp="afv8tizyrikw">
                Education
              </text>
            </g>

            {/* Workforce */}
            <g transform="translate(250, 250)" data-id="element-1068" data-mp="3svxq7oz8f3no">
              <circle cx="0" cy="0" r="12" fill="#FFFFFF" stroke="#123B5D" strokeWidth="2.5" data-id="element-1069" data-mp="3nvjg81e2ljn0" />
              <circle cx="0" cy="0" r="4" fill="#123B5D" data-id="element-1070" data-mp="135atl0hfkpu0" />
              <text x="20" y="4" fontSize="12" fill="#1F2A37" fontFamily="Inter" fontWeight="600" data-id="element-1071" data-mp="134st1rnqjpga">
                Workforce
              </text>
            </g>

            {/* Civil Society */}
            <g transform="translate(300, 350)" data-id="element-1072" data-mp="1k4c0v23epxdl">
              <circle cx="0" cy="0" r="10" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-1073" data-mp="1p5rpt58pgo19" />
              <text x="0" y="22" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-1074" data-mp="wg2ika5ejrr7">
                Civil Society
              </text>
            </g>

            {/* Government */}
            <g transform="translate(350, 220)" data-id="element-1075" data-mp="8p5ibumyyivs">
              <circle cx="0" cy="0" r="14" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-1076" data-mp="dpktqgnoxgb4" />
              <text x="22" y="4" fontSize="11" fill="#5B6675" fontFamily="Inter" fontWeight="500" data-id="element-1077" data-mp="17h1xml4pv2h">
                Government
              </text>
            </g>

            {/* Leadership */}
            <g transform="translate(400, 150)" data-id="element-1078" data-mp="3jeeyz5ksazgh">
              <circle cx="0" cy="0" r="18" fill="#FFFFFF" stroke="#123B5D" strokeWidth="3" data-id="element-1079" data-mp="3jf6mdt489qvo" />
              <circle cx="0" cy="0" r="26" stroke="#2E8C88" strokeWidth="1" strokeDasharray="2 2" data-id="element-1080" data-mp="39l7ox7f3mva2" />
              <text x="0" y="-32" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-1081" data-mp="2vnrhptozaxh9">
                Leadership
              </text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>;
}